import {Button, Card, CardBody, CardHeader, Input, Typography} from "@material-tailwind/react";
import React, {useEffect, useState, useCallback, useRef} from "react";
import {PaymentReportGrid} from "./grid";
import {submitPaymentReport} from "../../../services/report/payment";
import {LoadingOverlay} from "../../system/loading_overlay";
import {Toaster} from "../../../helper/Toaster";
import SelectInput from "../../system/select";
import {getPersonsNameAndFamily} from "../../../services/profile/profile";
import {PersianDateRangeInput} from "../../system/PersianDateRangeInput";
import {downloadFile} from "../../../config";
import {PaginateContext} from "../../../context/paginate";
import {CardFooterPaginator} from "../../system/cardFooterPaginator";
import {
  BsArrowCounterclockwise,
  BsCalendar3,
  BsCashStack,
  BsCheckCircle,
  BsChevronDown,
  BsCreditCard,
  BsFileEarmarkSpreadsheet,
  BsFunnel,
  BsHash,
  BsPerson,
  BsReceipt,
  BsSliders,
} from "react-icons/bs";
import "./payment-filters.css";


const paymentFilterSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: 44,
    borderRadius: 12,
    backgroundColor: "rgb(var(--color-app-raised))",
    borderColor: state.isFocused
      ? "rgb(var(--color-app-focus))"
      : "rgb(var(--color-app-border))",
    boxShadow: state.isFocused
      ? "0 0 0 3px rgb(var(--color-app-focus) / 0.14)"
      : "none",
    cursor: "pointer",
    transition: "border-color 160ms ease, box-shadow 160ms ease, background-color 160ms ease",
    "&:hover": {
      borderColor: state.isFocused
        ? "rgb(var(--color-app-focus))"
        : "rgb(var(--color-app-primary) / 0.48)",
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    paddingInlineEnd: 12,
    paddingInlineStart: 42,
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "rgb(var(--color-app-text-muted))",
    fontWeight: 500,
    opacity: 1,
  }),
  indicatorSeparator: () => ({display: "none"}),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: "rgb(var(--color-app-text-muted))",
  }),
};


export function PaymentReportManagement() {

  const [paymentReport, setPaymentReport] = useState([]);
  const [lastDataRefresh, setLastDataRefresh] = useState(Date.now);
  const [gridRowCount, setGridRowCount] = useState(10);
  const [gridCurrentPage, setGridCurrentPage] = useState(1);
  const [gridTotalRowCount, setGridTotalRowCount] = useState(0);
  const [sortBy, setSortBy] = useState("id")
  const [sortType, setSortType] = useState(false);
  const [from, setFrom] = useState((gridCurrentPage - 1) * gridRowCount + 1);
  const [to, setTo] = useState((gridCurrentPage) * gridRowCount);
  const [loading, setLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const debounceTimerRef = useRef(null);

  const [personId, setPersonId] = useState(null);
  const [actorName, setActorName] = useState(null);
  const [amount, setAmount] = useState(null);
  const [referenceCode, setReferenceCode] = useState(null);
  const [paymentOrigin, setPaymentOrigin] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [manualReference, setManualReference] = useState(null);
  const [endAtFrom, setEndAtFrom] = useState("")
  const [endAtTo, setEndAtTo] = useState("");
  const [status, setStatus] = useState(null);
  const paymentMethodOptions = [
    {id: "all", title: "همه"},
    {id: "payping", title: "درگاه پرداخت"},
    {id: "card_to_card", title: "کارت به کارت"},
    {id: "cash", title: "نقدی"},
    {id: "other", title: "سایر"},
  ];

  const paymentOriginOptions = [
    {id: "all", title: "همه"},
    {id: "direct", title: "پرداخت مستقیم"},
    {id: "admin_link", title: "لینک ارسالی"},
    {id: "manual", title: "ثبت دستی"},
  ];

  const activeFilterCount = [
    personId,
    actorName,
    amount,
    referenceCode,
    paymentOrigin && paymentOrigin !== "all" ? paymentOrigin : null,
    paymentMethod && paymentMethod !== "all" ? paymentMethod : null,
    manualReference,
    endAtFrom,
    endAtTo,
    status,
  ].filter(Boolean).length;


  useEffect(() => {
    if ((gridCurrentPage) * gridRowCount <= gridTotalRowCount) {
      setTo((gridCurrentPage) * gridRowCount)
    } else {
      setTo(gridTotalRowCount)
    }
    setFrom((gridCurrentPage - 1) * gridRowCount + 1)
  }, [gridCurrentPage, gridRowCount, gridTotalRowCount]);

  async function getData(data, paginate) {
    if (loading) return; // Prevent multiple simultaneous requests
    
    try {
      setLoading(true);
      const result = await submitPaymentReport(data, paginate);
      if (result.status === 200) {
        const {payment_report, total_count} = result.data;
        setPaymentReport(payment_report || []);
        setGridTotalRowCount(total_count || 0)
      } else {
        Toaster.failure(null, <Typography variant="small">
          {result.data?.message || 'خطا در دریافت گزارش'}
        </Typography>);
      }
    } catch (err) {
      Toaster.failure(null, <Typography variant="small">
        {err.message || 'خطا در دریافت گزارش'}
      </Typography>);
      console.error('Payment report fetch error:', err);
    } finally {
      setLoading(false);
    }
  }

  const filter = () => {
    const data = {}
    if (personId) {
      data.person_id = personId;
    }

    if (actorName) {
      data.sender_person_name = actorName;
    }

    if (amount) {
      data.amount = parseInt(amount);
    }

    if (referenceCode) {
      data.reference_code = referenceCode;
    }

    if (paymentOrigin && paymentOrigin !== "all") {
      data.payment_origin = paymentOrigin;
    }

    if (paymentMethod && paymentMethod !== "all") {
      data.payment_method = paymentMethod;
    }

    if (manualReference) {
      data.manual_reference = manualReference;
    }

    if (endAtFrom) {
      data.end_at_from = endAtFrom;
    }

    if (endAtTo) {
      data.end_at_to = endAtTo;
    }

    if (status) {
      data.status = status;
    }

    return data
  };

  const clearFilters = () => {
    setPersonId(null);
    setActorName(null);
    setAmount(null);
    setReferenceCode(null);
    setPaymentOrigin(null);
    setPaymentMethod(null);
    setManualReference(null);
    setEndAtFrom("");
    setEndAtTo("");
    setStatus(null);
    setGridCurrentPage(1);
    setLastDataRefresh(Date.now());
  };

  const exportReport = async () => {
    try {
      setIsExporting(true);
      const data = filter();
      downloadFile("payment_report_excel", data, "payments")
    } catch (err) {
      Toaster.failure(null, <Typography variant="small">
        خطا در خروجی گرفتن
      </Typography>);
      console.error('Error exporting:', err);
    } finally {
      setIsExporting(false);
    }
  };

  const debouncedGetData = useCallback((data, paginate) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    debounceTimerRef.current = setTimeout(() => {
      getData(data, paginate);
    }, 500); // 500ms debounce
  }, []);

  useEffect(() => {
    const data = filter()
    const paginate = new URLSearchParams({
      "sort_by": sortBy,
      "sort_type": sortType === false ? "DESC" : "ASC",
      "limit": parseInt(gridRowCount),
      "offset": (parseInt(gridCurrentPage) - 1) * parseInt(gridRowCount),
    }).toString()

    // For pagination and sorting, call immediately
    if (gridCurrentPage !== 1 || sortBy !== 'id' || sortType !== false) {
      getData(data, paginate);
    } else {
      // For filters, use debouncing
      debouncedGetData(data, paginate);
    }
    
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [personId, actorName, endAtFrom, endAtTo, status, paymentOrigin, paymentMethod, manualReference, gridCurrentPage, gridRowCount, sortBy, sortType, lastDataRefresh, debouncedGetData]);


  return (
    <PaginateContext.Provider
      value={{
        setSortBy: setSortBy,
        sortType: sortType,
        setSortType: setSortType,
        active: gridCurrentPage,
        setActive: setGridCurrentPage,
        totalRows: gridTotalRowCount,
        rowsCount: gridRowCount,
        setRowsCount: setGridRowCount,
        from: from,
        to: to,
      }}
    >
      <div className="flex w-full flex-col gap-4 p-4">
        <Card className="w-full border border-blue-gray-100 shadow-sm">
          <CardHeader floated={false} shadow={false} className="w-full rounded-none px-5 pt-4">
            <div className="mb-2 flex items-center gap-3">
              <span
                className="h-8 w-1 rounded-full"
                style={{backgroundColor: "rgb(var(--color-app-primary))"}}
              />
              <Typography variant="h5" color="blue-gray">تراکنش‌های واریز</Typography>
            </div>

          </CardHeader>
        <CardBody className="px-5 pb-5 pt-2">

          <div className="payment-filter-toolbar">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">

            <div className="payment-filter-control">
              <BsPerson className="payment-filter-control-icon"/>
              <SelectInput
                label={"کاربر"}
                placeholder={"کاربر"}
                placeholderIcon={null}
                hideLabel
                selectStyles={paymentFilterSelectStyles}
                defaultValue={personId}
                refreshSignal={personId}
                autoLoadService={getPersonsNameAndFamily}
                autoLoadRender={({name, family, phone}) => {
                  const fullName = `${name?.trim()} ${family?.trim()}`.trim();
                  return fullName + " - " + phone
                }}
                onChange={(value) => {
                  setPersonId(value);
                  setGridCurrentPage(1);
                }}
              />
            </div>

            <div className="payment-filter-control">
              <BsCheckCircle className="payment-filter-control-icon"/>
              <SelectInput
                label={"وضعیت"}
                placeholder={"وضعیت"}
                placeholderIcon={null}
                hideLabel
                selectStyles={paymentFilterSelectStyles}
                defaultValue={status}
                refreshSignal={status}
                options={[
                  {
                    "id": 'initiate',
                    "title": "در حال پرداخت"
                  },
                  {
                    "id": 'success',
                    "title": "پرداخت موفق"
                  },
                  {
                    "id": "failure",
                    "title": "پرداخت ناموفق"
                  },
                ]}
                onChange={(value) => {
                  setStatus(value);
                  setGridCurrentPage(1);
                }}
              />
            </div>

            <div className="payment-filter-control">
              <BsSliders className="payment-filter-control-icon"/>
              <SelectInput
                label={"نوع تراکنش"}
                placeholder={"نوع تراکنش"}
                placeholderIcon={null}
                hideLabel
                selectStyles={paymentFilterSelectStyles}
                defaultValue={paymentOrigin}
                refreshSignal={paymentOrigin}
                options={paymentOriginOptions}
                onChange={(value) => {
                  setPaymentOrigin(value);
                  setGridCurrentPage(1);
                }}
              />
            </div>

            <div className="payment-filter-control">
              <BsCreditCard className="payment-filter-control-icon"/>
              <SelectInput
                label={"روش پرداخت"}
                placeholder={"روش پرداخت"}
                placeholderIcon={null}
                hideLabel
                selectStyles={paymentFilterSelectStyles}
                defaultValue={paymentMethod}
                refreshSignal={paymentMethod}
                options={paymentMethodOptions}
                onChange={(value) => {
                  setPaymentMethod(value);
                  setGridCurrentPage(1);
                }}
              />
            </div>
            </div>

            <div className="payment-filter-actions">
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  variant="text"
                  onClick={() => setShowAdvancedFilters((current) => !current)}
                  className="payment-filter-toggle"
                  aria-expanded={showAdvancedFilters}
                >
                  <BsFunnel className="h-4 w-4"/>
                  <span>{showAdvancedFilters ? "بستن فیلترهای بیشتر" : "فیلترهای بیشتر"}</span>
                  <BsChevronDown
                    className={`h-4 w-4 transition-transform ${showAdvancedFilters ? "rotate-180" : ""}`}
                  />
                </Button>

                {activeFilterCount > 0 && (
                  <span className="payment-filter-count">
                    {activeFilterCount.toLocaleString("fa-IR")} فیلتر فعال
                  </span>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {activeFilterCount > 0 && (
                  <Button
                    size="sm"
                    variant="text"
                    onClick={clearFilters}
                    disabled={loading}
                    className="payment-filter-clear"
                  >
                    <BsArrowCounterclockwise className="h-4 w-4"/>
                    حذف فیلترها
                  </Button>
                )}

                {paymentReport.length > 0 && (
                  <Button
                    size="sm"
                    variant="outlined"
                    onClick={exportReport}
                    disabled={isExporting}
                    className="payment-filter-export"
                  >
                    <BsFileEarmarkSpreadsheet className="h-4 w-4"/>
                    {isExporting ? "در حال خروجی..." : "خروجی اکسل"}
                  </Button>
                )}
              </div>
            </div>

            <div className={`payment-advanced-region ${showAdvancedFilters ? "is-open" : ""}`}>
              <div className="payment-advanced-region-inner">
                <div className="payment-filter-panel">
                  <div className="payment-filter-panel-heading">
                    <span className="payment-filter-panel-icon">
                      <BsSliders className="h-4 w-4"/>
                    </span>
                    <div>
                      <Typography className="payment-filter-panel-title">فیلترهای تکمیلی</Typography>
                      <Typography className="payment-filter-panel-description">
                        برای جست‌وجوی دقیق‌تر میان تراکنش‌ها
                      </Typography>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <div className="payment-filter-field">
                  <BsCashStack className="payment-filter-field-icon"/>
                  <Input
                    variant="outlined"
                    className="payment-filter-input !pr-11"
                    value={amount ?? ""}
                    placeholder={"مبلغ تراکنش"}
                    onChange={(event) => setAmount(event.target.value)}
                    onBlur={() => {
                      setGridCurrentPage(1);
                      setLastDataRefresh(Date.now());
                    }}
                  />
                </div>

                <div className="payment-filter-field">
                  <BsPerson className="payment-filter-field-icon"/>
                  <Input
                    variant="outlined"
                    className="payment-filter-input !pr-11"
                    value={actorName ?? ""}
                    placeholder={"نام عامل"}
                    aria-label="نام عامل"
                    onChange={(event) => setActorName(event.target.value)}
                    onBlur={() => {
                      setGridCurrentPage(1);
                      setLastDataRefresh(Date.now());
                    }}
                  />
                </div>

                <div className="payment-filter-field">
                  <BsHash className="payment-filter-field-icon"/>
                  <Input
                    variant="outlined"
                    className="payment-filter-input !pr-11"
                    value={referenceCode ?? ""}
                    placeholder={"کد پیگیری"}
                    onChange={(event) => setReferenceCode(event.target.value)}
                    onBlur={() => {
                      setGridCurrentPage(1);
                      setLastDataRefresh(Date.now());
                    }}
                  />
                </div>

                <div className="payment-filter-field">
                  <BsReceipt className="payment-filter-field-icon"/>
                  <Input
                    variant="outlined"
                    className="payment-filter-input !pr-11"
                    value={manualReference ?? ""}
                    placeholder={"شماره رسید یا مرجع"}
                    onChange={(event) => setManualReference(event.target.value)}
                    onBlur={() => {
                      setGridCurrentPage(1);
                      setLastDataRefresh(Date.now());
                    }}
                  />
                </div>

                <div className="payment-filter-field sm:col-span-2 xl:col-span-2">
                  <BsCalendar3 className="payment-filter-field-icon"/>
                  <PersianDateRangeInput
                    fromDate={endAtFrom}
                    toDate={endAtTo}
                    setFromDate={(value) => {
                      setEndAtFrom(value);
                      setGridCurrentPage(1);
                    }}
                    setToDate={(value) => {
                      setEndAtTo(value);
                      setGridCurrentPage(1);
                    }}
                    placeholder="بازه تاریخ پرداخت"
                    inputClassName="payment-filter-input !pr-11"
                  />
                </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      <Card className="payment-table-card w-full">
        <CardBody className="w-full overflow-x-auto p-0">

          {loading && <LoadingOverlay />}

          <div className="relative">
            <PaymentReportGrid
              paymentReport={paymentReport}
              setSortBy={setSortBy}
              sortType={sortType}
              setSortType={setSortType}
            />
          </div>

        </CardBody>
        <CardFooterPaginator />
      </Card>
      </div>
    </PaginateContext.Provider>
  );
}
